import React, { useState, useEffect } from 'react';
import { Search, Settings, FileText, TrendingUp, Zap, Satellite, Brain, Book, BarChart3, ChartNetwork, Loader2, ChevronDown, ChevronUp, ExternalLink, Calendar, Quote, Users, Hash, Lightbulb, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const PathfinderUI = () => {
  const [query, setQuery] = useState('');
  const [topK, setTopK] = useState(10);
  const [keywords, setKeywords] = useState('');
  const [toggles, setToggles] = useState(['Keywords']);
  const [promptType, setPromptType] = useState('Multi-paper');
  const [ragType, setRagType] = useState('Semantic + HyDE + CoHERE');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [papers, setPapers] = useState([]);
  const [consensus, setConsensus] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [expandedPaper, setExpandedPaper] = useState(null);
  const [activeTab, setActiveTab] = useState('results');
  const [fetchingCitation, setFetchingCitation] = useState(null);
  const [plotData, setPlotData] = useState(null);

  const promptOptions = [
    { value: 'Single-paper', label: 'Single Paper Focus', icon: FileText, desc: 'Questions that need ~1 paper to answer' },
    { value: 'Multi-paper', label: 'Multi-Paper Analysis', icon: Book, desc: 'Synthesize across multiple papers' },
    { value: 'Bibliometric', label: 'Bibliometric Analysis', icon: BarChart3, desc: 'Citation and trend analysis, ADS queries' },
    { value: 'Broad but nuanced', label: 'Broad & Nuanced', icon: Brain, desc: 'Complex, multi-faceted questions' },
    { value: 'Deep Research (BETA)', label: 'Deep Research', icon: Lightbulb, desc: 'Comprehensive research with atomic questions' }
  ];

  const ragOptions = [
    { value: 'Semantic Search', label: 'Semantic Search', desc: 'Basic semantic similarity' },
    { value: 'Semantic + HyDE', label: 'Semantic + HyDE', desc: 'With hypothetical document embedding' },
    { value: 'Semantic + CoHERE', label: 'Semantic + Rerank', desc: 'With Cohere reranking' },
    { value: 'Semantic + HyDE + CoHERE', label: 'Full Pipeline', desc: 'All enhancement methods' }
  ];

  const weightOptions = [
    { value: 'Keywords', label: 'Keywords', desc: 'Weight by keyword relevance' },
    { value: 'Time', label: 'Recency', desc: 'Prefer recent papers' },
    { value: 'Citations', label: 'Citations', desc: 'Weight by citation count' }
  ];

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    setResults(null);
    setPapers([]);
    setConsensus('');
    setQuestionType('');
    setPlotData(null);
    
    try {
      // Method 1: Using the @gradio/client library (recommended)
      const { Client } = await import('@gradio/client');
      const app = await Client.connect("kiyer/pathfinder");
      
      const result = await app.predict("/run_pathfinder", {
        query: query,
        top_k: topK,
        extra_keywords: keywords,
        toggles: toggles,
        prompt_type: promptType,
        rag_type: ragType
      });
      
      console.log('API Result:', result);
      
      // Parse the result based on your Gradio app's return format
      if (result && result.data) {
        const [papersData, generatedAnswer, consensusData, questionAnalysis, plotData] = result.data;
        
        // Parse papers data
        if (papersData) {
          let papers = [];
          
          // Handle different possible data formats
          if (Array.isArray(papersData)) {
            // If papersData is an array of paper objects/arrays
            papers = papersData.map((paper, index) => {
              // Handle both object and array formats
              if (typeof paper === 'object' && !Array.isArray(paper)) {
                return {
                  id: index + 1,
                  title: paper.title || paper['title'] || "Unknown Title",
                  authors: Array.isArray(paper.authors) ? paper.authors : 
                          (paper.authors ? paper.authors.toString().split(', ') : ['Unknown Author']),
                  abstract: paper.abstract || paper['abstract'] || "No abstract available",
                  date: paper.date ? new Date(paper.date) : new Date('2023-01-01'),
                  citations: parseInt(paper.cites || paper['cites'] || 0),
                  relevance: parseFloat(paper.Relevance || paper['Relevance'] || 0),
                  keywords: Array.isArray(paper.keywords) ? paper.keywords : 
                           (paper.keywords ? paper.keywords.split(', ') : ['astronomy']),
                  adsLink: paper['ADS Link'] || paper.adsLink || "#",
                  bibcode: paper.ads_id || paper['ads_id'] || "Unknown"
                };
              } else if (Array.isArray(paper)) {
                // If paper is an array of values
                return {
                  id: index + 1,
                  title: paper[4] || "Unknown Title",
                  authors: paper[5] ? paper[5].split(', ') : ['Unknown Author'],
                  abstract: paper[6] || "No abstract available",
                  date: paper[2] ? new Date(paper[2]) : new Date('2023-01-01'),
                  citations: parseInt(paper[3] || 0),
                  relevance: parseFloat(paper[1] || 0),
                  keywords: paper[7] ? paper[7].split(', ') : ['astronomy'],
                  adsLink: paper[0] || "#",
                  bibcode: paper[8] || "Unknown"
                };
              }
              return null;
            }).filter(Boolean);
          } else if (papersData.data && Array.isArray(papersData.data)) {
            // If it's a DataFrame-like structure with data array
            papers = papersData.data.map((row, index) => ({
              id: index + 1,
              title: row[papersData.headers?.indexOf('title')] || row[4] || "Unknown Title",
              authors: [row[papersData.headers?.indexOf('authors')] || row[5] || "Unknown Author"],
              abstract: row[papersData.headers?.indexOf('abstract')] || row[6] || "No abstract available",
              date: new Date(row[papersData.headers?.indexOf('date')] || row[2] || '2023-01-01'),
              citations: parseInt(row[papersData.headers?.indexOf('cites')] || row[3] || 0),
              relevance: parseFloat(row[papersData.headers?.indexOf('Relevance')] || row[1] || 0),
              keywords: [row[papersData.headers?.indexOf('keywords')] || row[7] || "astronomy"],
              adsLink: row[papersData.headers?.indexOf('ADS Link')] || row[0] || "#",
              bibcode: row[papersData.headers?.indexOf('ads_id')] || row[8] || "Unknown"
            }));
          }
          
          setPapers(papers);
        }
        
        setResults(generatedAnswer || "Search completed successfully.");
        setConsensus(consensusData || "No consensus analysis available.");
        setQuestionType(questionAnalysis || "No question type analysis available.");
        setPlotData(plotData || null);
      }
      
    } catch (error) {
      console.error('Search failed:', error);
      
      // Method 2: Fallback to direct HTTP API call
      try {
        console.log('Trying direct HTTP API call...');
        
        // Step 1: Submit the job
        const submitResponse = await fetch('https://kiyer-pathfinder.hf.space/call/run_pathfinder', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: [query, topK, keywords, toggles, promptType, ragType]
          })
        });
        
        if (!submitResponse.ok) {
          throw new Error(`Submit failed! status: ${submitResponse.status}`);
        }
        
        const submitResult = await submitResponse.text();
        const eventId = submitResult.replace('event_id:', '').replace('"', '').replace('"', '').trim();
        
        // Step 2: Poll for results
        let attempts = 0;
        const maxAttempts = 30;
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          try {
            const resultsResponse = await fetch(`https://kiyer-pathfinder.hf.space/call/run_pathfinder/${eventId}`);
            
            if (resultsResponse.ok) {
              const resultsText = await resultsResponse.text();
              
              // Parse streaming response
              const lines = resultsText.split('\n').filter(line => line.startsWith('data: '));
              if (lines.length > 0) {
                const lastLine = lines[lines.length - 1];
                try {
                  const data = JSON.parse(lastLine.replace('data: ', ''));
                  
                  if (data && data.length >= 5) {
                    const [papersData, generatedAnswer, consensusData, questionAnalysis, plotData] = data;
                    
                    // Use similar parsing logic as above
                    if (papersData) {
                      const papers = papersData.map ? papersData.map((paper, index) => ({
                        id: index + 1,
                        title: paper.title || "Unknown Title",
                        authors: Array.isArray(paper.authors) ? paper.authors : [paper.authors || "Unknown Author"],
                        abstract: paper.abstract || "No abstract available",
                        date: new Date(paper.date || '2023-01-01'),
                        citations: parseInt(paper.cites || 0),
                        relevance: parseFloat(paper.Relevance || 0),
                        keywords: Array.isArray(paper.keywords) ? paper.keywords : [paper.keywords || "astronomy"],
                        adsLink: paper['ADS Link'] || "#",
                        bibcode: paper.ads_id || "Unknown"
                      })) : [];
                      setPapers(papers);
                    }
                    
                    setResults(generatedAnswer || "Search completed via fallback method.");
                    setConsensus(consensusData || "No consensus analysis available.");
                    setQuestionType(questionAnalysis || "No question type analysis available.");
                    setPlotData(plotData || null);
                    setIsLoading(false);
                    return;
                  }
                } catch (parseError) {
                  console.log('Parse error on attempt', attempts, parseError);
                }
              }
            }
          } catch (pollError) {
            console.log('Poll error on attempt', attempts, pollError);
          }
          
          attempts++;
        }
        
        throw new Error('Timeout waiting for results from direct API');
        
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
        
        // Show demo data if all API methods fail
        const mockPapers = [
          {
            id: 1,
            title: "Deep Learning Approaches to Galaxy Classification in Large Sky Surveys",
            authors: ["Smith, J.", "Johnson, A.", "Lee, K."],
            abstract: "We present a comprehensive study of deep learning methods for automated galaxy classification using data from the Sloan Digital Sky Survey. Our convolutional neural network achieves 95% accuracy in distinguishing between spiral, elliptical, and irregular galaxies, significantly outperforming traditional methods.",
            date: new Date('2023-08-15'),
            citations: 127,
            relevance: 0.94,
            keywords: ["machine learning", "galaxy classification", "deep learning", "SDSS"],
            adsLink: "https://ui.adsabs.harvard.edu/abs/2023ApJ...900..123S/abstract",
            bibcode: "2023ApJ...900..123S"
          },
          {
            id: 2,
            title: "Machine Learning in Astronomy: A Review of Current Applications",
            authors: ["Brown, M.", "Davis, R."],
            abstract: "This review summarizes the current state of machine learning applications in astronomy, covering topics from exoplanet detection to cosmological parameter estimation. We discuss both successes and limitations of current approaches.",
            date: new Date('2023-05-20'),
            citations: 89,
            relevance: 0.87,
            keywords: ["machine learning", "astronomy", "review", "applications"],
            adsLink: "https://ui.adsabs.harvard.edu/abs/2023ARA&A..61..234B/abstract",
            bibcode: "2023ARA&A..61..234B"
          }
        ];
        
        setPapers(mockPapers);
        setResults(`API connection failed. Error: ${error.message}\n\nShowing demo data. Your query was: "${query}"\n\nTo connect to your actual backend, please ensure the Gradio space is running and accessible.`);
        setConsensus("Demo consensus: Unable to connect to live API. This is demonstration data showing how the interface works.");
        setQuestionType(`Demo analysis: Your question "${query}" appears to be about ${query.toLowerCase().includes('machine learning') ? 'machine learning applications' : 'astronomy research'}. This is demonstration text since the API is not accessible.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleWeight = (weight) => {
    setToggles(prev => 
      prev.includes(weight) 
        ? prev.filter(w => w !== weight)
        : [...prev, weight]
    );
  };

  const exportSession = () => {
  const sessionData = {
    version: "1.0",
    timestamp: new Date().toISOString(),
    query,
    settings: {
      topK,
      keywords,
      toggles,
      promptType,
      ragType
    },
    results: {
      answer: results,
      papers,
      consensus,
      questionType,
      activeTab,
      plotData
    }
  };
  
  const dataStr = JSON.stringify(sessionData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `pathfinder-session-${new Date().toISOString().slice(0,10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

const importSession = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const sessionData = JSON.parse(e.target.result);
      
      // Restore query and settings
      setQuery(sessionData.query || '');
      setTopK(sessionData.settings?.topK || 10);
      setKeywords(sessionData.settings?.keywords || '');
      setToggles(sessionData.settings?.toggles || ['Keywords']);
      setPromptType(sessionData.settings?.promptType || 'Multi-paper');
      setRagType(sessionData.settings?.ragType || 'Semantic + HyDE + CoHERE');
      
      // Restore results
      setResults(sessionData.results?.answer || null);
      setConsensus(sessionData.results?.consensus || '');
      setQuestionType(sessionData.results?.questionType || '');
      setPlotData(sessionData.results?.plotData || null);
      setActiveTab(sessionData.results?.activeTab || 'results');
      
      // Restore papers with date conversion
      if (sessionData.results?.papers) {
        const restoredPapers = sessionData.results.papers.map(paper => ({
          ...paper,
          date: new Date(paper.date)
        }));
        setPapers(restoredPapers);
      }
      
      alert('Session restored successfully!');
    } catch (error) {
      console.error('Error importing session:', error);
      alert('Error importing session. Please check the file format.');
    }
  };
  reader.readAsText(file);
  
  // Reset input so same file can be imported again
  event.target.value = '';
};

const getCitation = async (bibcode) => {
  try {
    // Use the Gradio client to fetch citation
    const { Client } = await import('@gradio/client');
    const client = await Client.connect("kiyer/getbib");
    const result = await client.predict("/process_bibcodes", {
      bibcode_input: bibcode
    });
    
    // The result.data should contain [status_message, file_path]
    // We need to fetch the actual file content
    if (result && result.data && result.data[1]) {
      const fileInfo = result.data[1];
      // fileInfo is typically an object with a url property
      const fileUrl = fileInfo.url || fileInfo;
      
      // Fetch the actual .bib file content
      const response = await fetch(fileUrl);
      const bibContent = await response.text();
      
      return bibContent;
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching citation for ${bibcode}:`, error);
    return null;
  }
};

const exportCitations = async () => {
  if (!papers || papers.length === 0) {
    alert('No papers to export citations for.');
    return;
  }
  
  setIsLoading(true);
  
  try {
    // Collect all valid adsIds (extracted from adsLink similar to how it's done in the Papers tab)
    const bibcodes = papers
      .map(paper => {
        let adsId = paper.bibcode || "ADS";
        let adsUrl = paper.adsLink;
        const mdLinkMatch = typeof adsUrl === "string" && adsUrl.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (mdLinkMatch) {
          adsId = mdLinkMatch[1];
        }
        return adsId;
      })
      .filter(bibcode => bibcode && bibcode !== "Unknown" && bibcode !== "ADS");
    
    if (bibcodes.length === 0) {
      alert('No valid bibcodes found for citation export.');
      return;
    }
    
    // Join bibcodes with commas for batch processing
    const bibcodeInput = bibcodes.join(', ');
    
    // Use the Gradio client to fetch all citations at once
    const { Client } = await import('@gradio/client');
    const client = await Client.connect("kiyer/getbib");
    const result = await client.predict("/process_bibcodes", {
      bibcode_input: bibcodeInput
    });
    
    if (result && result.data && result.data[1]) {
      const fileInfo = result.data[1];
      const fileUrl = fileInfo.url || fileInfo;
      
      // Fetch the actual .bib file content
      const response = await fetch(fileUrl);
      const bibContent = await response.text();
      
      // Create and download .bib file
      const blob = new Blob([bibContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pathfinder-citations-${new Date().toISOString().slice(0,10)}.bib`;
      link.click();
      URL.revokeObjectURL(url);
      
      alert(`Successfully exported ${bibcodes.length} citation(s)!`);
    } else {
      alert('Could not retrieve citations from the API.');
    }
    
  } catch (error) {
    console.error('Error exporting citations:', error);
    alert(`Error exporting citations: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};

const exportSingleCitation = async (paper, bibcode) => {
  if (!bibcode || bibcode === "Unknown" || bibcode === "ADS") {
    alert('No valid bibcode available for this paper.');
    return;
  }
  
  setFetchingCitation(paper.id);
  
  try {
    const citation = await getCitation(bibcode);
    
    if (!citation) {
      alert('Could not retrieve citation for this paper.');
      return;
    }
    
    // Create and download .bib file
    const blob = new Blob([citation], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${bibcode}.bib`;
    link.click();
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error exporting citation:', error);
    alert(`Error exporting citation: ${error.message}`);
  } finally {
    setFetchingCitation(null);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* <div className="flex items-center justify-between h-16"> */}
          <div className="flex flex-col items-center justify-center h-58 py-4">
            <img
              src="/pathfinder_logo.png"
              alt="Pathfinder Logo"
              className="w-[400px] h-auto object-contain mx-auto"
              style={{ minWidth: '420px', maxwidth: '100%' }}
            />
            {/* <h1 className="text-xl font-bold bg-gradient-to-r from-blue-900 to-indigo-900 bg-clip-text text-transparent">
              v2.pfdr.app
            </h1>
            <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              LLM enabled Literature Search
            </span> */}
          </div>
            {/* </div> */}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-slate-200">
          <div className="space-y-6">
            {/* Main Search Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Research Question
              </label>
              <div className="relative">
                <input
                  type="text"
                  id='main-search'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="LLM enabled literature search - e.g., 'How is machine learning used in astronomy?'"
                  className="w-full px-4 py-4 pr-12 text-lg border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              </div>
            </div>

            {/* Configuration Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Search Parameters */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center">
                  <Settings className="w-4 h-4 mr-2" />
                  Search Parameters
                </h3>
                
                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Papers to retrieve: {topK}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={topK}
                    onChange={(e) => setTopK(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Additional Keywords
                  </label>
                  <input
                    type="text"
                    value={keywords}
                    id = 'extra-keywords'
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="comma-separated keywords"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">Weight by</label>
                  <div className="space-y-2">
                    {weightOptions.map((option) => (
                      <label key={option.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={toggles.includes(option.value)}
                          onChange={() => toggleWeight(option.value)}
                          className="mr-3 rounded text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="text-sm text-slate-700">{option.label}</span>
                          <p className="text-xs text-slate-500">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Prompt Specialization */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center">
                  <Brain className="w-4 h-4 mr-2" />
                  Analysis Type
                </h3>
                <div className="space-y-2">
                  {promptOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <label key={option.value} className="flex items-start cursor-pointer group">
                        <input
                          type="radio"
                          value={option.value}
                          checked={promptType === option.value}
                          onChange={(e) => setPromptType(e.target.value)}
                          className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <Icon className="w-4 h-4 mr-2 text-slate-500 group-hover:text-blue-600 transition-colors" />
                            <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                              {option.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 ml-6">{option.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* RAG Method */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center">
                  <Zap className="w-4 h-4 mr-2" />
                  Search Method
                </h3>
                <div className="space-y-2">
                  {ragOptions.map((option) => (
                    <label key={option.value} className="flex items-start cursor-pointer group">
                      <input
                        type="radio"
                        value={option.value}
                        checked={ragType === option.value}
                        onChange={(e) => setRagType(e.target.value)}
                        className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                          {option.label}
                        </span>
                        <p className="text-xs text-slate-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={handleSearch}
                disabled={!query.trim() || isLoading}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Running pathfinder...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    <span>Let's go!</span>
                  </>
                )}
              </button>
            </div>

            {/* Export/Import Session Buttons */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
              <div className="flex justify-end space-x-3">
                <input
                  type="file"
                  accept=".json"
                  onChange={importSession}
                  className="hidden"
                  id="import-session"
                />
                <label
                  htmlFor="import-session"
                  className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 cursor-pointer transition-all flex items-center space-x-2 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span className="text-sm font-medium">Import Session</span>
                </label>
                
                {results && papers.length > 0 && (
                  <>
                    {/* <button
                      onClick={exportCitations}
                      disabled={isLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-all flex items-center space-x-2 shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <span className="text-sm font-medium">
                        {isLoading ? 'Fetching...' : 'Export Citations (.bib)'}
                      </span>
                    </button> */}
                    
                    <button
                      onClick={exportSession}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2 shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span className="text-sm font-medium">Export Session</span>
                    </button>
                  </>
                )}
              </div>
            </div>


          </div>
        </div>

        {/* Results Section */}
        {(isLoading || results) && (
          <div className="space-y-6">
            {/* Loading State */}
                  {isLoading && (
                    <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
                    <div className="flex items-center justify-center space-x-4">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <div className="text-center">
                      {(() => {
                        const loadingMessages = [
                        "Rooting around in the paper pile...",
                        "Consulting the literature oracles...",
                        "Dusting off some old journals...",
                        "Synthesizing research insights...",
                        "Scanning the event horizon...",
                        "Peering into the abyss...",
                        "Asking a wise potato...",
                        "Invoking arcane rituals...",
                        "Is there really an answer to this?...",
                        ];
                        const randomMsg = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
                        return (
                        <>
                          <p className="text-lg font-medium text-slate-700">{randomMsg}</p>
                          <p className="text-sm text-slate-500">Please allow for ~30 sec for the analysis to finish!</p>
                        </>
                        );
                      })()}
                      </div>
                    </div>
                    </div>
                  )}

                  {/* Results Tabs */}
            {results && (
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-200">
                  <div className="flex space-x-0">
                    {[
                      { id: 'results', label: 'Answer/summary', icon: Satellite },
                      { id: 'papers', label: `Papers (${papers.length})`, icon: FileText },
                      { id: 'consensus', label: 'Consensus', icon: TrendingUp },
                      { id: 'analysis', label: 'Question Analysis', icon: AlertCircle },
                      { id: 'plot', label: 'Data Plot', icon: ChartNetwork }
                    ].map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
                            activeTab === tab.id
                              ? 'border-blue-600 text-blue-600 bg-blue-50'
                              : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="p-6">
                  
                  {/* Papers Tab */}
                    {activeTab === 'papers' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Retrieved papers, sorted by relevance
                      </h3>
                      {papers.map((paper, index) => {
                        // If adsLink is a markdown link, extract [text](url)
                        let adsId = paper.bibcode || "ADS";
                        let adsUrl = paper.adsLink;
                        const mdLinkMatch = typeof adsUrl === "string" && adsUrl.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
                        if (mdLinkMatch) {
                          adsId = mdLinkMatch[1];
                          adsUrl = mdLinkMatch[2];
                        }
                        return (
                          <div key={paper.id} className="border border-slate-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-start space-x-4">
                                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-lg font-semibold text-slate-800 mb-2">{paper.title}</h4>
                                    <div className="flex items-center space-x-4 text-sm text-slate-600 mb-3">
                                      <span className="flex items-center">
                                        <Users className="w-4 h-4 mr-1" />
                                        {[paper.authors.toString().split(',')[0],' et al.']}
                                      </span>
                                      <span className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-1" />
                                        {paper.date.getFullYear()}
                                      </span>
                                      <span className="flex items-center">
                                        <Quote className="w-4 h-4 mr-1" />
                                        {paper.citations} citations
                                      </span>
                                      <span className="flex items-center">
                                        <TrendingUp className="w-4 h-4 mr-1" />
                                        {(paper.relevance * 100).toFixed(1)}% relevance
                                      </span>
                                    </div>
                                    
                                    {(expandedPaper === paper.id) && (
                                      <div className="space-y-3">
                                        <div>
                                          <h5 className="font-medium text-slate-700 mb-2">Abstract</h5>
                                          <p className="text-slate-600 leading-relaxed">{paper.abstract}</p>
                                        </div>
                                        <div>
                                          <h5 className="font-medium text-slate-700 mb-2">Keywords</h5>
                                          <div className="flex flex-wrap gap-2">
                                            {paper.keywords.map((keyword, idx) => (
                                              <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-sm">
                                                {keyword}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                <button
                                  onClick={() => exportSingleCitation(paper, adsId)}
                                  disabled={fetchingCitation === paper.id}
                                  className="p-2 text-slate-400 hover:text-green-600 transition-colors disabled:text-slate-300 disabled:cursor-not-allowed"
                                  title="Download citation (.bib)"
                                >
                                  {fetchingCitation === paper.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                  )}
                                </button>
                                <a
                                  href={adsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                  title="View on ADS"
                                >
                                  <span className="mr-1 text-xs font-mono">{adsId}</span>
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                                <button
                                  onClick={() => setExpandedPaper(expandedPaper === paper.id ? null : paper.id)}
                                  className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                >
                                  {expandedPaper === paper.id ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Generated Answer Tab */}
                  {activeTab === 'results' && (
                    <div className="prose max-w-none">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                          <Satellite className="w-5 h-5 mr-2 text-blue-600" />
                          Answer / Summary
                        </h3>
                        <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {<ReactMarkdown>{results}</ReactMarkdown>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Consensus Tab */}
                  {activeTab === 'consensus' && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2" />
                        Research Consensus
                      </h3>
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {consensus}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Plot Tab */}
                  {activeTab === 'plot' && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                        <BarChart3 className="w-5 h-5 mr-2" />
                        Visualization
                      </h3>
                      {plotData ? (
                        <div className="bg-white rounded-xl p-6 border border-slate-200">
                          {plotData.plot ? (
                            <img 
                              src={plotData.plot} 
                              alt="Research visualization" 
                              className="w-full h-auto rounded-lg shadow-md"
                            />
                          ) : plotData['plot:'] ? (
                            // Handle the typo case where key is "plot:" instead of "plot"
                            <img 
                              src={plotData['plot:']} 
                              alt="Research visualization" 
                              className="w-full h-auto rounded-lg shadow-md"
                            />
                          ) : (
                            <div className="text-slate-600">
                              <p className="mb-2">Unexpected plot data format:</p>
                              <pre className="mt-4 p-4 bg-slate-50 rounded overflow-auto text-xs">
                                {JSON.stringify(plotData, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-slate-50 rounded-xl p-6 text-center text-slate-500">
                          No visualization data available for this query.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Question Analysis Tab */}
                  {activeTab === 'analysis' && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        Question Type Analysis
                      </h3>
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                        <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {questionType}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PathfinderUI;